import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './LanguageSwitcher.css';

// Country flag SVG icons for better quality
const FLAG_ICONS = {
  en: (
    <svg className="flag-icon" viewBox="0 0 640 480">
      <path fill="#bd3d44" d="M0 0h640v480H0"/>
      <path stroke="#fff" strokeWidth="37" d="M0 55.3h640M0 129h640M0 203h640M0 277h640M0 351h640M0 425h640"/>
      <path fill="#192f5d" d="M0 0h364.8v258.5H0"/>
    </svg>
  ),
  it: (
    <svg className="flag-icon" viewBox="0 0 640 480">
      <g fillRule="evenodd" strokeWidth="1pt">
        <path fill="#fff" d="M0 0h640v480H0z"/>
        <path fill="#009246" d="M0 0h213.3v480H0z"/>
        <path fill="#ce2b37" d="M426.7 0H640v480H426.7z"/>
      </g>
    </svg>
  ),
  fr: (
    <svg className="flag-icon" viewBox="0 0 640 480">
      <path fill="#fff" d="M0 0h640v480H0z"/>
      <path fill="#002654" d="M0 0h213.3v480H0z"/>
      <path fill="#ce1126" d="M426.7 0H640v480H426.7z"/>
    </svg>
  ),
  ar: (
    <svg className="flag-icon" viewBox="0 0 640 480">
      <defs>
        <clipPath id="tn-a">
          <path fillOpacity=".7" d="M-85.3 0h682.6v512H-85.3z"/>
        </clipPath>
      </defs>
      <g fillRule="evenodd" clipPath="url(#tn-a)" transform="translate(80) scale(.9375)">
        <path fill="#e70013" d="M-128 0h768v512h-768z"/>
        <path fill="#fff" d="M385.8 255.8a129.1 129.1 0 1 1-258.2 0 129.1 129.1 0 0 1 258.2 0z"/>
        <path fill="#e70013" d="M256.7 341.4a85.7 85.7 0 0 1 0-171.3c11.8 0 25.3 2.8 34.4 9.5-62.6 2.3-78.5 55.5-78.5 76.9 0 21.4 16.2 69.1 78.5 76.9-9.2 6.7-22.7 9.5-34.4 9.5z"/>
        <path fill="#e70013" d="m332.1 291.8-38.9-14.2-25.7 32.4 1.5-41.3-38.8-14.5 39.8-11.4 1.7-41.3 23.2 34.3 39.8-11-25.5 32.5z"/>
      </g>
    </svg>
  ),
};

// Language data with localized names and country codes
const LANGUAGES_DATA = {
  en: {
    name: 'English',
    nativeName: 'English',
    countryCode: 'US',
    dir: 'ltr',
    localizedNames: {
      en: 'English',
      it: 'Inglese',
      fr: 'Anglais',
      ar: 'الإنجليزية',
    }
  },
  it: {
    name: 'Italian',
    nativeName: 'Italiano',
    countryCode: 'IT',
    dir: 'ltr',
    localizedNames: {
      en: 'Italian',
      it: 'Italiano',
      fr: 'Italien',
      ar: 'الإيطالية',
    }
  },
  fr: {
    name: 'French',
    nativeName: 'Français',
    countryCode: 'FR',
    dir: 'ltr',
    localizedNames: {
      en: 'French',
      it: 'Francese',
      fr: 'Français',
      ar: 'الفرنسية',
    }
  },
  ar: {
    name: 'Arabic',
    nativeName: 'العربية',
    countryCode: 'TN',
    dir: 'rtl',
    localizedNames: {
      en: 'Arabic',
      it: 'Arabo',
      fr: 'Arabe',
      ar: 'العربية',
    }
  },
};

// Order of languages to display
const LANGUAGE_ORDER = ['en', 'it', 'fr', 'ar'];

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLangCode, setCurrentLangCode] = useState(i18n.language.split('-')[0]);

  // Update current language when i18n changes
  useEffect(() => {
    setCurrentLangCode(i18n.language.split('-')[0]);
  }, [i18n.language]);

  const currentLanguage = LANGUAGES_DATA[currentLangCode] || LANGUAGES_DATA.en;
  const CurrentFlagIcon = FLAG_ICONS[currentLangCode] || FLAG_ICONS.en;

  const handleLanguageChange = (langCode) => {
    const selectedLang = LANGUAGES_DATA[langCode] || LANGUAGES_DATA.en;
    i18n.changeLanguage(langCode);
    document.documentElement.dir = selectedLang.dir;
    document.documentElement.lang = langCode;
    
    // Update body direction for RTL support
    if (selectedLang.dir === 'rtl') {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
    
    setIsOpen(false);
  };

  const getLocalizedLanguageName = (langCode) => {
    const lang = LANGUAGES_DATA[langCode];
    if (!lang) return langCode.toUpperCase();
    
    return lang.localizedNames[currentLangCode] || lang.nativeName;
  };

  // Close dropdown when clicking outside - FIXED
  useEffect(() => {
    const handleClickOutside = (event) => {
      const switcher = event.target.closest('.language-switcher-mini');
      if (!switcher && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Use mousedown instead of click to prevent conflicts
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Toggle dropdown
  const toggleDropdown = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  // Prevent dropdown from closing when clicking inside
  const handleDropdownClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="language-switcher-mini">
      <motion.button
        className="current-language-button"
        onClick={toggleDropdown}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={`Current language: ${currentLanguage.name}. Click to change language`}
        aria-expanded={isOpen}
      >
        <div className="flag-container">
          {CurrentFlagIcon}
        </div>
        <span className="language-code">{currentLanguage.countryCode}</span>
        <motion.span 
          className="arrow"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          ▼
        </motion.span>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="language-dropdown"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ 
              duration: 0.2,
              type: "spring",
              stiffness: 300,
              damping: 25
            }}
            onClick={handleDropdownClick}
            role="menu"
            aria-label="Language selection"
          >
            <div className="dropdown-header">
              <div className="current-language-display">
                <div className="current-flag-container">
                  {CurrentFlagIcon}
                </div>
                <div className="current-language-info">
                  <span className="current-language-name">
                    {getLocalizedLanguageName(currentLangCode)}
                  </span>
                  <span className="current-language-code">
                    {currentLanguage.countryCode}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="divider"></div>
            
            <div className="language-options-grid">
              {LANGUAGE_ORDER
                .filter(langCode => langCode !== currentLangCode)
                .map((langCode) => {
                  const lang = LANGUAGES_DATA[langCode];
                  const FlagIcon = FLAG_ICONS[langCode] || FLAG_ICONS.en;
                  
                  return (
                    <motion.button
                      key={langCode}
                      className="language-option"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLanguageChange(langCode);
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      role="menuitem"
                      aria-label={`Switch to ${lang.name}`}
                    >
                      <div className="option-flag-container">
                        {FlagIcon}
                      </div>
                      <div className="option-language-info">
                        <span className="option-language-name">
                          {getLocalizedLanguageName(langCode)}
                        </span>
                        <span className="option-language-code">
                          {lang.countryCode}
                        </span>
                      </div>
                    </motion.button>
                  );
                })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
